import { resolveEmphasisContours, resolveEmphasisPointer, resolveGuidanceCenter, resolveGuidanceItems, withGuidanceEmphasis } from '@toolproof-core/visualization/runtime/_pure/visual-guidance';
import type { EmphasisMark, VisualEmphasis, VisualGuidance, VisualTarget } from '@toolproof-core/visualization/_contract/guidance';
import type { SpaceDerivationFrame } from '@toolproof-core/visualization/derivation/_contract/derivation';

import { digitAtPower } from '@/adapters/primitive/_pure/model/natural/columnArithmetic';
import { toBooleanChannelSegmentKey, toBooleanOperandSegmentKey, toBooleanProfileConstellationKey, toBooleanTravellerSegmentKey,
    toNaturalDigitItemKey, toPrimitiveAxisRodMarkerItemKey, toPrimitiveHubConstellationKey, toPrimitivePowerConstellationKey } from '@/adapters/primitive/_pure/derivation/keyCodec';
import { toDepthOrderedWheels } from '@/adapters/primitive/_pure/derivation/wheels';
import type { PrimitiveEmphasisConfig, PrimitiveEmphasisTargetClass, PrimitiveWheelState } from '@/adapters/primitive/_contract/config';
import type { PrimitiveConceptTarget } from '@/adapters/primitive/_contract/episode';
import type { PrimitiveConceptPresentationRequest, PrimitivePresentationContext, PrimitivePresentationRequest } from '@/adapters/primitive/_contract/presentation';
import type { PrimitiveVisualTarget as PlanVisualTarget } from '../../_contract/scenePlan';

type SemanticKeyTarget = Extract<PlanVisualTarget, { targetKind: 'natural-digit' | 'boolean-channel' | 'boolean-operand' | 'boolean-traveller' }>;

/** Semantic targets name a part by meaning and encode to the raw key the projection renders. */
export function encodePrimitiveSemanticTarget(target: SemanticKeyTarget): Extract<PlanVisualTarget, { targetKind: 'item' | 'constellation' }> {
    switch (target.targetKind) {
        case 'natural-digit': return { targetKind: 'item', itemKey: toNaturalDigitItemKey({ wheelRole: target.wheelRole as PrimitiveWheelState['role'],
            power: target.power, digit: target.digit, ...(target.variant === undefined ? {} : { variant: target.variant }) }) };
        case 'boolean-channel': return { targetKind: 'constellation',
            constellationKey: toBooleanProfileConstellationKey(toBooleanChannelSegmentKey(target.pathKey, target.section)) };
        case 'boolean-operand': return { targetKind: 'constellation',
            constellationKey: toBooleanProfileConstellationKey(toBooleanOperandSegmentKey(target.pathKey, target.wheelRole)) };
        case 'boolean-traveller': return { targetKind: 'constellation',
            constellationKey: toBooleanProfileConstellationKey(toBooleanTravellerSegmentKey(target.pathKey, target.wheelRole)) };
    }
}

export function resolvePrimitiveVisualTarget(target: PlanVisualTarget, wheels: readonly PrimitiveWheelState[],
    frame: SpaceDerivationFrame): readonly VisualTarget[] {
    if (target.targetKind === 'natural-digit' || target.targetKind === 'boolean-channel'
        || target.targetKind === 'boolean-operand' || target.targetKind === 'boolean-traveller') {
        return resolvePrimitiveVisualTarget(encodePrimitiveSemanticTarget(target), wheels, frame);
    }
    if (target.targetKind === 'item') {
        const item = frame.itemCollections.flatMap(collection => collection.itemCollectionKind === 'entity'
            ? collection.items : []).find(item => item.itemKey === target.itemKey);
        if (!item) throw new Error(`Episode item target '${target.itemKey}' does not exist at its anchor.`);
        return [target];
    }
    if (target.targetKind === 'constellation') {
        if (!frame.constellations.some(entry => entry.constellationKind === 'entity'
            && entry.constellationKey === target.constellationKey)) throw new Error(`Invalid episode constellation target '${target.constellationKey}'.`);
        return [target];
    }
    if (target.targetKind === 'natural-counted-cells') {
        const wheel = wheels.find(wheel => wheel.role === target.wheelRole && wheel.variant === target.variant
            && wheel.powers.includes(target.power));
        if (!wheel) throw new Error('Episode counted-cell target has no matching wheel.');
        return countedCellTargets(wheel, target.power, frame);
    }
    const part = target.targetKind === 'natural-band' || target.targetKind === 'boolean-disc';
    const selected = wheels.filter(wheel => target.targetKind === 'primitive-rod'
        ? wheel.variant === undefined
        : wheel.role === target.wheelRole && wheel.variant === target.variant
            && (target.targetKind !== 'natural-band' || wheel.powers.includes(target.power)));
    if (!selected.length) throw new Error(`Episode ${target.targetKind} target has no matching wheel.`);
    if (part) {
        const expected = target.targetKind === 'natural-band' ? 'natural' : 'boolean';
        for (const wheel of selected) {
            const kind = wheel.valueKind === 'placeholder' ? wheel.resourceKind : wheel.valueKind ?? 'natural';
            if (kind !== expected) throw new Error(`Episode ${target.targetKind} target names the ${kind} wheel '${wheel.role}'.`);
        }
    }
    const targets = selected.flatMap(wheel => {
        if (wheel.valueKind === 'placeholder' && !part) {
            const constellationKey = toPrimitiveHubConstellationKey({ wheelRole: wheel.role });
            if (!frame.constellations.some(entry => entry.constellationKey === constellationKey)) {
                throw new Error(`Episode wheel '${wheel.role}' has no placeholder hub.`);
            }
            return [{ targetKind: 'constellation' as const, constellationKey }];
        }
        const powers = target.targetKind === 'natural-band' ? [target.power]
            : target.targetKind === 'boolean-disc' ? [0] : wheel.powers;
        return powers.map(power => {
            if (!wheel.powers.includes(power)) throw new Error(`Episode target power '${power}' is absent on '${wheel.role}'.`);
            const constellationKey = toPrimitivePowerConstellationKey({ wheelRole: wheel.role, power,
                ...(wheel.variant === undefined ? {} : { variant: wheel.variant }) });
            if (!frame.constellations.some(entry => entry.constellationKey === constellationKey)) {
                throw new Error(`Episode band target '${constellationKey}' has no rendered constellation.`);
            }
            return { targetKind: 'constellation' as const, constellationKey };
        });
    });
    if (!targets.length) throw new Error('Episode visual target resolves to no bands.');
    return targets;
}

/** Item and digit targets are cells; constellation and Boolean profile targets are bands; wheels and rods are whole things. */
function planTargetClass(target: PlanVisualTarget): PrimitiveEmphasisTargetClass {
    switch (target.targetKind) {
        case 'item': case 'natural-counted-cells': case 'natural-digit': return 'cells';
        case 'constellation': case 'natural-band': case 'boolean-disc':
        case 'boolean-channel': case 'boolean-operand': case 'boolean-traveller': return 'band';
        case 'primitive-wheel': case 'primitive-rod': return 'whole';
    }
}

function primitiveEmphasis(targets: readonly VisualTarget[], targetClass: PrimitiveEmphasisTargetClass,
    emphasis: PrimitiveEmphasisConfig, caption?: string, overrideMarks?: readonly EmphasisMark[]): VisualEmphasis {
    const marks = overrideMarks ?? emphasis.marks[targetClass];
    const shown = caption !== undefined && emphasis.showCaptions && marks.includes('pointer');
    return { targets, color: emphasis.color, marks, ...(shown ? { caption } : {}),
        ...(targetClass === 'whole' && marks.includes('pointer') ? { pointerGapPixels: 40 } : {}),
        lifetime: 'while-present' };
}

export function resolvePrimitivePresentation(
    args: PrimitivePresentationContext & PrimitivePresentationRequest,
): VisualGuidance {
    const { presentation: selected, frame, wheels, key } = args;
    const emphases: VisualEmphasis[] = [];
    const lineContours: NonNullable<VisualGuidance['lineContours']>[number][] = [];
    let orbit: VisualGuidance['orbit'];
    let pointerSweep: VisualGuidance['pointerSweep'];
    for (const effect of selected.effects) {
        if (effect.effectKind === 'emphasis' && effect.target.targetKind === 'primitive-rod'
            && effect.marks?.length === 1 && effect.marks[0] === 'contour') {
            lineContours.push(rodContour(args.emphasis.color));
            continue;
        }
        const targets = resolvePrimitiveVisualTarget(effect.target, wheels, frame);
        if (effect.effectKind === 'emphasis') emphases.push(primitiveEmphasis(targets, planTargetClass(effect.target), args.emphasis, effect.caption, effect.marks));
        else if (effect.effectKind === 'pointer-sweep') {
            pointerSweep = rodPointerSweep(wheels, effect.durationMs, args.emphasis.color);
        }
        else if (args.orbitState !== 'inactive') {
            if (args.orbitState !== 'stationary') throw new Error('Episode orbit requires a stationary anchor.');
            orbit = { targets, durationMs: effect.durationMs, angleRadians: effect.angleRadians };
        }
    }
    return { key, ...(emphases.length || pointerSweep ? { emphases, pointerPlacement: selected.pointerPlacement ?? args.emphasis.pointerPlacement } : {}),
        ...(selected.textPanel ? { textPanel: selected.textPanel } : {}),
        ...(lineContours.length ? { lineContours } : {}),
        ...(pointerSweep ? { pointerSweep } : {}),
        ...(orbit ? { orbit } : {}) };
}

export function validatePrimitivePresentation(
    args: PrimitivePresentationContext & Pick<PrimitivePresentationRequest, 'presentation'> & { stationary: boolean },
): void {
    const { presentation: segment, wheels, frame, emphasis } = args;
    const emphases: VisualEmphasis[] = [];
    const lineContours: NonNullable<VisualGuidance['lineContours']>[number][] = [];
    for (const effect of segment.effects) {
        if (effect.effectKind === 'emphasis' && effect.target.targetKind === 'primitive-rod'
            && effect.marks?.length === 1 && effect.marks[0] === 'contour') {
            lineContours.push(rodContour(emphasis.color));
            continue;
        }
        const targets = resolvePrimitiveVisualTarget(effect.target, wheels, frame);
        resolveGuidanceItems(frame, targets);
        if (effect.effectKind === 'emphasis') emphases.push(primitiveEmphasis(targets, planTargetClass(effect.target), emphasis, effect.caption, effect.marks));
        else resolveGuidanceCenter(frame, targets);
    }
    const sweep = segment.effects.find(effect => effect.effectKind === 'pointer-sweep');
    if (sweep && !args.stationary) throw new Error('Pointer sweeps require a stationary beat.');
    const guidance: VisualGuidance = { key: 'validation', emphases, lineContours,
        pointerPlacement: segment.pointerPlacement ?? emphasis.pointerPlacement,
        ...(sweep ? { pointerSweep: rodPointerSweep(wheels, sweep.durationMs, emphasis.color) } : {}) };
    withGuidanceEmphasis(frame, guidance);
    resolveEmphasisPointer(frame, guidance);
    resolveEmphasisContours(frame, guidance);
}

function rodContour(color: string): NonNullable<VisualGuidance['lineContours']>[number] {
    return { key: 'primitive-operation-rod', color,
        from: { targetKind: 'item', itemKey: toPrimitiveAxisRodMarkerItemKey({ endpoint: 'front' }) },
        to: { targetKind: 'item', itemKey: toPrimitiveAxisRodMarkerItemKey({ endpoint: 'back' }) } };
}

function rodPointerSweep(wheels: readonly PrimitiveWheelState[], durationMs: number, color: string): NonNullable<VisualGuidance['pointerSweep']> {
    const stationary = toDepthOrderedWheels(wheels.filter(wheel => wheel.variant === undefined && !wheel.roleless));
    if (stationary.length < 2) throw new Error('A rod pointer sweep requires at least two stationary wheels.');
    return {
        from: { targetKind: 'item', itemKey: toPrimitiveAxisRodMarkerItemKey({ endpoint: 'front' }) },
        to: { targetKind: 'item', itemKey: toPrimitiveAxisRodMarkerItemKey({ endpoint: 'back' }) },
        avoid: stationary.slice(1, -1).flatMap(wheel => wheel.powers.map(power => ({
            targetKind: 'constellation' as const,
            constellationKey: toPrimitivePowerConstellationKey({ wheelRole: wheel.role, power }),
        }))),
        durationMs, color,
    };
}

/** Counted cells follow the same rule the digit factory uses to colour them. */
function countedCellTargets(wheel: PrimitiveWheelState, power: number, frame: SpaceDerivationFrame): readonly VisualTarget[] {
    if (wheel.valueKind !== undefined && wheel.valueKind !== 'natural') throw new Error('Counted cells require a Natural wheel.');
    if (!wheel.powers.includes(power)) throw new Error(`Counted-cell target power '${power}' is absent on '${wheel.role}'.`);
    const countedThrough = wheel.countedThroughDigitByPower?.[power] ?? digitAtPower({ value: wheel.value, power });
    const digits = wheel.countedDigitsByPower?.[power]
        ?? Array.from({ length: countedThrough }, (_, index) => index + 1);
    if (!digits.length) throw new Error(`Counted-cell target power '${power}' counts no cells.`);
    return digits.map(digit => resolvePrimitiveVisualTarget({ targetKind: 'item',
        itemKey: toNaturalDigitItemKey({ wheelRole: wheel.role, power, digit,
            ...(wheel.variant === undefined ? {} : { variant: wheel.variant }) }) }, [wheel], frame)[0]!);
}

/** Concept wheels are unique per value kind; they carry an operation role only to reuse the rod layout. */
export function resolvePrimitiveConceptTarget(
    target: PrimitiveConceptTarget, args: PrimitivePresentationContext,
): { targets: readonly VisualTarget[]; targetClass: PrimitiveEmphasisTargetClass } {
    if (target.targetKind === 'natural-assembly-band') {
        const role = target.bandKind === 'unit' ? 'identity-input' : 'identity-output';
        const wheel = args.wheels.find(wheel => wheel.role === role && wheel.variant === undefined);
        if (!wheel) throw new Error(`Concept Assembly target '${target.bandKind}' has no matching wheel.`);
        const power = Math.max(...wheel.powers);
        const targets = target.part === 'band'
            ? resolvePrimitiveVisualTarget({ targetKind: 'natural-band', wheelRole: role, power }, args.wheels, args.frame)
            : target.part === 'zero'
                ? resolvePrimitiveVisualTarget({ targetKind: 'item',
                    itemKey: toNaturalDigitItemKey({ wheelRole: role, power, digit: 0 }) }, args.wheels, args.frame)
                : countedCellTargets(wheel, power, args.frame);
        return { targets, targetClass: target.part === 'band' ? 'band' : 'cells' };
    }
    if (target.targetKind === 'boolean-disc') {
        const wheels = args.wheels.filter(wheel => wheel.variant === undefined && wheel.valueKind === 'boolean');
        if (wheels.length !== 1) throw new Error('Concept Boolean disc requires exactly one Boolean wheel.');
        const targets = resolvePrimitiveVisualTarget({ targetKind: 'boolean-disc', wheelRole: wheels[0]!.role },
            args.wheels, args.frame);
        return { targets, targetClass: 'band' };
    }
    const wheel = args.wheels.filter(entry => entry.variant === undefined
        && (entry.valueKind ?? 'natural') === (target.targetKind === 'primitive-wheel' ? target.valueKind : 'natural'));
    if (wheel.length !== 1) throw new Error(`Concept target '${target.targetKind}' requires exactly one wheel.`);
    const selected = wheel[0]!;
    const targets = target.targetKind === 'primitive-wheel'
        ? resolvePrimitiveVisualTarget({ targetKind: 'primitive-wheel', wheelRole: selected.role }, args.wheels, args.frame)
        : target.targetKind === 'natural-angular-band'
            ? selected.powers.flatMap(power => resolvePrimitiveVisualTarget({ targetKind: 'item',
                itemKey: toNaturalDigitItemKey({ wheelRole: selected.role, power, digit: target.digit }) }, args.wheels, args.frame))
        : target.targetKind === 'natural-band' || target.cells === 'all'
            ? resolvePrimitiveVisualTarget({ targetKind: 'natural-band', wheelRole: selected.role, power: target.power }, args.wheels, args.frame)
            : target.cells === 'zero'
                ? resolvePrimitiveVisualTarget({ targetKind: 'item',
                    itemKey: toNaturalDigitItemKey({ wheelRole: selected.role, power: target.power, digit: 0 }) }, args.wheels, args.frame)
                : countedCellTargets(selected, target.power, args.frame);
    return { targets, targetClass: target.targetKind === 'primitive-wheel' ? 'whole'
        : target.targetKind === 'natural-band' || target.targetKind === 'natural-angular-band' ? 'band' : 'cells' };
}

export function resolvePrimitiveConceptPresentation(
    args: PrimitivePresentationContext & PrimitiveConceptPresentationRequest,
): VisualGuidance {
    const { presentation: segment } = args;
    const emphases: VisualEmphasis[] = [];
    let orbit: VisualGuidance['orbit'];
    for (const effect of segment.effects) {
        const { targets, targetClass } = resolvePrimitiveConceptTarget(effect.target, args);
        if (effect.effectKind === 'emphasis') {
            emphases.push(primitiveEmphasis(targets, targetClass, args.emphasis, effect.caption, effect.marks));
        } else if (args.orbitState !== 'inactive') {
            if (args.orbitState !== 'stationary') throw new Error('Episode orbit requires a stationary anchor.');
            orbit = { targets, durationMs: effect.durationMs, angleRadians: effect.angleRadians };
        }
    }
    return { key: args.key,
        ...(segment.textPanel ? { textPanel: segment.textPanel } : {}),
        ...(orbit ? { orbit } : {}),
        ...(emphases.length ? { emphases, pointerPlacement: segment.pointerPlacement ?? args.emphasis.pointerPlacement } : {}) };
}
